import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, useAdminAuth } from '../context/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import AnalyticsPanel from '../components/AnalyticsPanel';
import { CalendarDays, MessageSquare, ArrowRight, Loader2 } from 'lucide-react';

const T = { vert: '#1e4a1a', or: '#c4a76c', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', border: '#e8e5e0', cardBg: '#ffffff' };

const AdminDashboardPage = () => {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try { setStats(await adminApi.get('/api/admin/dashboard')); } catch (err) { console.error('Failed to load dashboard:', err); }
      setLoading(false);
    };
    load();
  }, []);

  const getStatusBadge = (status) => {
    const map = {
      pending: { label: 'En attente', bg: 'rgba(196,167,108,0.12)', color: '#a07c3a' },
      confirmed: { label: 'Confirme', bg: 'rgba(22,163,74,0.08)', color: '#16a34a' },
      cancelled: { label: 'Annule', bg: 'rgba(239,68,68,0.08)', color: '#dc2626' },
      new: { label: 'Nouveau', bg: 'rgba(59,130,246,0.08)', color: '#2563eb' },
      read: { label: 'Lu', bg: '#f3f4f6', color: T.textMuted },
      replied: { label: 'Repondu', bg: 'rgba(22,163,74,0.08)', color: '#16a34a' },
    };
    const b = map[status] || map.pending;
    return (
      <span className="px-2.5 py-1 text-[10px] font-semibold rounded-full" style={{ backgroundColor: b.bg, color: b.color, fontFamily: "'Montserrat', sans-serif" }}>
        {b.label}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-7xl" data-testid="admin-dashboard">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>
            Bonjour, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
            Vue d'ensemble de votre activite
          </p>
        </div>

        <AnalyticsPanel compact />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Bookings */}
          <div className="rounded-2xl overflow-hidden bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-[13px] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                Dernieres reservations
              </h2>
              <Link to="/admin/audience" className="text-[11px] flex items-center gap-1 font-medium" style={{ color: T.vert }}>
                Voir tout <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div>
              {stats?.recent_bookings?.length > 0 ? (
                stats.recent_bookings.map((b, i) => (
                  <div key={b.booking_id} className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: i < stats.recent_bookings.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                    <div>
                      <p className="text-[13px] font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                        {b.first_name} {b.last_name}
                      </p>
                      <p className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: T.textMuted }}>
                        <CalendarDays className="w-3 h-3" /> {b.date} a {b.time}
                      </p>
                    </div>
                    {getStatusBadge(b.status)}
                  </div>
                ))
              ) : (
                <div className="py-12 text-center">
                  <CalendarDays className="w-8 h-8 mx-auto mb-2" style={{ color: T.border }} />
                  <p className="text-[12px]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Aucune reservation</p>
                </div>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="rounded-2xl overflow-hidden bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-[13px] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                Derniers messages
              </h2>
              <Link to="/admin/audience" className="text-[11px] flex items-center gap-1 font-medium" style={{ color: T.vert }}>
                Voir tout <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div>
              {stats?.recent_contacts?.length > 0 ? (
                stats.recent_contacts.map((c, i) => (
                  <div key={c.contact_id} className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: i < stats.recent_contacts.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                    <div>
                      <p className="text-[13px] font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                        {c.name}
                      </p>
                      <p className="text-[11px] truncate max-w-[200px]" style={{ color: T.textMuted }}>{c.subject}</p>
                    </div>
                    {getStatusBadge(c.status)}
                  </div>
                ))
              ) : (
                <div className="py-12 text-center">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2" style={{ color: T.border }} />
                  <p className="text-[12px]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Aucun message</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
